import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  CardContent,
  InputAdornment,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Language as UrlIcon,
  ContactPage as ContactPageIcon,
  Sms as SmsIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Google as GoogleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { QRCodeDestinationType } from '../../../types';
import AttachFileUrlModal from 'src/components/AttachFileUrlModal/view';
import VcfDisplayFromUrl, { VcfDisplayFromText } from 'src/components/VcfViewer/view';
import { useCompanyPhoneApi, CompanyPhone } from 'src/modules/companies/api/CompanyPhoneApi';
import { useSelector } from 'react-redux';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useAssetsApi } from 'src/modules/assetLibrary/api/AssetsApi';
import { DashedDropZone } from './DashedDropZone';
import { Dropdown, Panel, TextInput } from 'src/UIKit';
import { TintedChoiceCard } from './TintedChoiceCard.styles';

interface ScanDestinationSectionProps {
  destinationType: QRCodeDestinationType;
  destinationData: Record<string, unknown>;
  onDestinationTypeChange: (type: QRCodeDestinationType) => void;
  onDestinationDataChange: (data: Record<string, unknown>) => void;
  onBlur?: () => void;
  isEditMode: boolean;
}

const DESTINATION_TYPES: { value: QRCodeDestinationType; label: string; icon: React.ReactNode }[] =
  [
    { value: 'URL', label: 'URL', icon: <UrlIcon fontSize="large" /> },
    { value: 'SMS', label: 'SMS', icon: <SmsIcon fontSize="large" /> },
    { value: 'Contact Card', label: 'Contact', icon: <ContactPageIcon fontSize="large" /> },
    { value: 'Instagram', label: 'Instagram', icon: <InstagramIcon fontSize="large" /> },
    { value: 'Facebook', label: 'Facebook', icon: <FacebookIcon fontSize="large" /> },
    { value: 'Google Review', label: 'Google Review', icon: <GoogleIcon fontSize="large" /> },
  ];

export const ScanDestinationSection: React.FC<ScanDestinationSectionProps> = ({
  destinationType,
  destinationData,
  onDestinationTypeChange,
  onDestinationDataChange,
  onBlur,
  isEditMode,
}) => {
  const [showFileModal, setShowFileModal] = useState(false);
  const [companyPhones, setCompanyPhones] = useState<CompanyPhone[]>([]);
  const [phonesLoading, setPhonesLoading] = useState(true);
  const { getFileContent } = useAssetsApi();

  const currentCompanyId = useSelector(getCurrentCompanyId);
  const companyPhoneApi = useCompanyPhoneApi();

  useEffect(() => {
    const loadCompanyPhones = async () => {
      if (!currentCompanyId) return;

      setPhonesLoading(true);
      try {
        const response = await companyPhoneApi.getCompanyPhones(currentCompanyId);
        setCompanyPhones(response.data);

        if (
          response.data.length === 1 &&
          !destinationData.phoneNumber &&
          destinationType === 'SMS'
        ) {
          updateDestinationData('phoneNumber', response.data[0].number);
          if (onBlur) {
            setTimeout(() => onBlur(), 100);
          }
        }
      } catch (error) {
        console.error('Failed to load company phones:', error);
        setCompanyPhones([]);
      } finally {
        setPhonesLoading(false);
      }
    };

    loadCompanyPhones();
  }, [currentCompanyId, companyPhoneApi]);

  const handleDestinationTypeClick = (type: QRCodeDestinationType) => {
    if (!isEditMode) {
      onDestinationTypeChange(type);

      if (type === 'SMS' && companyPhones.length === 1) {
        onDestinationDataChange({ phoneNumber: companyPhones[0].number });
      } else {
        onDestinationDataChange({});
      }
    }
  };

  const updateDestinationData = (key: string, value: unknown) => {
    onDestinationDataChange({
      ...destinationData,
      [key]: value,
    });
  };

  const renderDestinationFields = () => {
    switch (destinationType) {
      case 'URL':
        return (
          <TextInput
            label="URL"
            disabled={isEditMode}
            value={(destinationData.url as string) || ''}
            onChange={(_, value) => updateDestinationData('url', value)}
            onBlur={e => {
              let url = e.target.value.trim();
              if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
                url = 'https://' + url;
                updateDestinationData('url', url);
              }
              if (onBlur) onBlur();
            }}
            placeholder="example.com or https://example.com"
            margin="none"
          />
        );

      case 'SMS':
        return (
          <Stack gap={2.5}>
            {companyPhones.length === 1 ? (
              <TextInput
                label="Phone Number"
                value={companyPhones[0]?.number || ''}
                onChange={() => undefined}
                disabled
                margin="none"
              />
            ) : (
              <Dropdown
                label="Phone Number"
                value={(destinationData.phoneNumber as string) || ''}
                options={companyPhones.map(phone => ({ value: phone.number, title: phone.number }))}
                onChange={(_, value) => {
                  updateDestinationData('phoneNumber', value);
                  if (onBlur) setTimeout(() => onBlur(), 100);
                }}
                required
                disabled={phonesLoading || isEditMode}
                margin="none"
              />
            )}
            <TextInput
              label="Message"
              value={(destinationData.message as string) || ''}
              onChange={(_, value) => updateDestinationData('message', value)}
              onBlur={() => {
                if (onBlur) onBlur();
              }}
              placeholder="Enter pre-filled message"
              multiline
              disabled={isEditMode}
              rows={3}
              required
              margin="none"
            />
          </Stack>
        );

      case 'Contact Card': {
        const vcardData = destinationData.vcardData as string | undefined;
        const contactCard = destinationData.contactCard as
          | { name?: string; url?: string; vcardData?: string }
          | undefined;

        const handleDeleteContact = () => {
          onDestinationDataChange({
            ...destinationData,
            vcardData: undefined,
            contactCard: undefined,
          });
          if (onBlur) onBlur();
        };

        return (
          <>
            {vcardData && (
              <Box mb={2.5}>
                <Typography variant="subtitle2" mb={1} fontWeight={600}>
                  Contact Card:
                </Typography>
                <VcfDisplayFromText vcfText={vcardData} onDelete={handleDeleteContact} />
              </Box>
            )}
            <Box mb={2.5}>
              {/* Display VCF Viewer if contact card is selected in create mode */}
              {!isEditMode && contactCard?.url && (
                <Panel sx={{ mb: 2, position: 'relative' }}>
                  <IconButton
                    onClick={handleDeleteContact}
                    disabled={isEditMode}
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="subtitle2" mb={1} fontWeight={600}>
                    Selected Contact Card:
                  </Typography>
                  <VcfDisplayFromUrl vcfUrl={contactCard.url} />
                </Panel>
              )}

              {/* Upload/Select Button - only in create mode and when no contact is attached */}
              {!isEditMode && !vcardData && !contactCard?.url && (
                <>
                  <DashedDropZone
                    sx={{ p: 2, textAlign: 'center' }}
                    onClick={() => setShowFileModal(true)}
                  >
                    <UploadIcon fontSize="large" color="disabled" />
                    <Typography variant="body2" color="textSecondary">
                      Click to upload or select contact card
                    </Typography>
                    <Typography variant="caption" display="block" mt={0.5}>
                      Supported formats: .vcf
                    </Typography>
                  </DashedDropZone>

                  {showFileModal && (
                    <AttachFileUrlModal
                      show={showFileModal}
                      setShowModal={setShowFileModal}
                      handleFileChange={async (file: { name?: string; url?: string }) => {
                        if (file.url) {
                          try {
                            const response = await getFileContent(file.url);
                            const text = await response.data.text();
                            updateDestinationData('vcardData', text);
                            if (onBlur) {
                              onBlur();
                            }
                          } catch (error) {
                            console.error('Failed to fetch vCard data:', error);
                          }
                        }
                        setShowFileModal(false);
                      }}
                      allowedFormats=".vcf"
                      onlyUpload={false}
                      fileAcceptTypes={{ 'text/vcard': ['.vcf'] }}
                    />
                  )}
                </>
              )}
            </Box>
          </>
        );
      }

      case 'Instagram':
        return (
          <TextInput
            disabled={isEditMode}
            label="Instagram Handle"
            value={(destinationData.handle as string) || ''}
            onChange={(_, value) => updateDestinationData('handle', value)}
            onBlur={onBlur}
            placeholder="username"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  @
                </InputAdornment>
              ),
            }}
            margin="none"
          />
        );

      case 'Facebook':
        return (
          <TextInput
            disabled={isEditMode}
            label="Facebook Page"
            value={(destinationData.facebookPage as string) || ''}
            onChange={(_, value) => updateDestinationData('facebookPage', value)}
            onBlur={onBlur}
            placeholder="yourpage"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  https://www.facebook.com/
                </InputAdornment>
              ),
            }}
            helperText={isEditMode ? '' : 'Enter the page name that comes after facebook.com/'}
            margin="none"
          />
        );

      case 'Google Review':
        return (
          <TextInput
            disabled={isEditMode}
            label="Google Review URL"
            value={(destinationData.url as string) || ''}
            onChange={(_, value) => updateDestinationData('url', value)}
            onBlur={e => {
              let url = e.target.value.trim();
              if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
                url = 'https://' + url;
                updateDestinationData('url', url);
              }
              if (onBlur) onBlur();
            }}
            placeholder="https://g.page/r/..."
            helperText={isEditMode ? '' : 'Enter your Google Review page URL'}
            margin="none"
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        {DESTINATION_TYPES.map(type => (
          <Grid item xs={3} sm={4} md={4} key={type.value}>
            <TintedChoiceCard
              selected={destinationType === type.value}
              onClick={() => handleDestinationTypeClick(type.value)}
              sx={{
                cursor: isEditMode ? 'not-allowed' : 'pointer',
                opacity: isEditMode && destinationType !== type.value ? 0.5 : 1,
              }}
            >
              <CardContent>
                <Stack alignItems="center" textAlign="center">
                  <Box color={destinationType === type.value ? 'primary.main' : 'grey.600'} mb={1}>
                    {type.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={destinationType === type.value ? 'primary.main' : 'grey.800'}
                  >
                    {type.label}
                  </Typography>
                </Stack>
              </CardContent>
            </TintedChoiceCard>
          </Grid>
        ))}
      </Grid>
      <Box mb={2.5}>{renderDestinationFields()}</Box>
    </Box>
  );
};
